import React, { useState } from 'react';
import styled from 'styled-components';
import PageHero from '../components/PageHero';
import { useForm } from 'react-hook-form';
import { 
  FiMail, 
  FiPhone, 
  FiMapPin, 
  FiSend, 
  FiCheckCircle,
  FiX
} from 'react-icons/fi';
import { useSettings } from '../contexts/SettingsContext';
import ScrollAnimation from '../components/ScrollAnimation';
import { contactService } from '../services/api';

const ContactContainer = styled.div`
  /* remove global offset to eliminate white gap under fixed header */
  margin-top: 0;
  min-height: 100vh;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  overflow-x: hidden;
  width: 100%;
`;

// Replaced by PageHero

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  position: relative;
  z-index: 1;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 ${props => props.theme.spacing.md};
  }
`;

// PageHero handles heading/subtitle styling

const Section = styled.section`
  padding: ${props => props.theme.spacing['2xl']} 0 ${props => props.theme.spacing['4xl']};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(239, 68, 68, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.xl} 0 ${props => props.theme.spacing['3xl']};
  }
`;

const ContactSection = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing['4xl']};

  h2 {
    font-size: ${props => props.theme.fontSizes['3xl']};
    color: #1f2937;
    margin-bottom: ${props => props.theme.spacing.lg};
    font-weight: 700;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 3px;
      background: ${props => props.theme.colors.gradient.primary};
      border-radius: 2px;
    }
  }

  .subtitle {
    font-size: ${props => props.theme.fontSizes.lg};
    color: #6b7280;
    margin-bottom: ${props => props.theme.spacing['3xl']};
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing['4xl']};
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  /* Ensure cards take equal height so buttons can align */
  align-items: stretch;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    max-width: 400px;
  }
`;

const ContactCard = styled.div`
  background: white;
  padding: ${props => props.theme.spacing['2xl']};
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #f3f4f6;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  /* Make content stack vertically and allow button to stick to bottom */
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  height: 100%;
  min-height: 280px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    min-height: 260px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.theme.colors.gradient.primary};
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    border-color: ${props => props.theme.colors.primary};

    &::before {
      transform: scaleX(1);
    }

    .icon {
      transform: scale(1.1);
    }

    .contact-action {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
    }
  }

  .icon {
    width: 52px;
    height: 52px;
    background: ${props => props.theme.colors.gradient.primary};
    border-radius: ${props => props.theme.borderRadius.full};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto ${props => props.theme.spacing.lg};
    color: white;
    font-size: ${props => props.theme.fontSizes.xl};
    transition: transform 0.3s ease;
  }

  h3 {
    font-size: ${props => props.theme.fontSizes.lg};
    color: #1f2937;
    margin-bottom: ${props => props.theme.spacing.sm};
    font-weight: 600;
  }

  p {
    color: #6b7280;
    font-size: ${props => props.theme.fontSizes.sm};
    margin-bottom: ${props => props.theme.spacing.lg};
    line-height: 1.5;
  }

  .contact-action {
    background: ${props => props.theme.colors.primary};
    color: white;
    padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
    border: none;
    border-radius: ${props => props.theme.borderRadius.md};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${props => props.theme.spacing.xs};
    /* Push button to the bottom so all cards align */
    margin-top: auto;
    align-self: center;
    white-space: nowrap;
    width: 200px;
    max-width: 90%;

    &:hover {
      background: #dc2626;
    }

    svg {
      font-size: ${props => props.theme.fontSizes.sm};
    }
  }
`;

const MessageSection = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing['3xl']};

  h2 {
    font-size: ${props => props.theme.fontSizes['3xl']};
    color: #1f2937;
    margin-bottom: ${props => props.theme.spacing.lg};
    font-weight: 700;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 3px;
      background: ${props => props.theme.colors.gradient.primary};
      border-radius: 2px;
    }
  }

  .subtitle {
    font-size: ${props => props.theme.fontSizes.lg};
    color: #6b7280;
    margin-bottom: ${props => props.theme.spacing['3xl']};
    line-height: 1.6;
  }
`;

const FormWrapper = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 ${props => props.theme.spacing.md};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }

  &.single {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  &:focus-within {
    label {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const Label = styled.label`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.sm};
  display: block;
  letter-spacing: 0.025em;
  text-transform: capitalize;

  .required {
    color: ${props => props.theme.colors.primary};
    font-size: ${props => props.theme.fontSizes.lg};
  }
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  border: 2px solid ${props => props.error ? props.theme.colors.primary : '#e5e7eb'};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.fontSizes.base};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #fafbfc;
  width: 100%;
  line-height: 1.5;
  font-weight: 500;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    background: white;
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: #9ca3af;
    background: white;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }

  &:invalid:not(:placeholder-shown) {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  border: 2px solid ${props => props.error ? props.theme.colors.primary : '#e5e7eb'};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.fontSizes.base};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: #fafbfc;
  width: 100%;
  line-height: 1.6;
  font-weight: 500;
  min-height: 140px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    background: white;
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: #9ca3af;
    background: white;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
`;


const ErrorMessage = styled.span`
  color: ${props => props.theme.colors.primary};
  font-size: ${props => props.theme.fontSizes.sm};
  margin-top: ${props => props.theme.spacing.sm};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};

  &::before {
    content: '⚠';
    font-size: ${props => props.theme.fontSizes.base};
  }
`;

const SuccessMessage = styled.div`
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  color: #155724;
  padding: ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.xl};
  border: 1px solid #c3e6cb;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing['2xl']};
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.15);
  font-weight: ${props => props.theme.fontWeights.medium};

  svg {
    color: #28a745;
    font-size: ${props => props.theme.fontSizes.xl};
    flex-shrink: 0;
  }
`;

const SubmitButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing['2xl']};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  min-height: 48px;
  width: 100%;

  &:hover:not(:disabled) {
    background: #dc2626;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .loading-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: ${props => props.theme.spacing.lg};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.md};
    align-items: flex-start;
    padding-top: 60px;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing['2xl']};
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  box-shadow: ${props => props.theme.shadows['2xl']};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.lg};
    max-height: 85vh;
    border-radius: ${props => props.theme.borderRadius.lg};
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  
  h3 {
    margin: 0;
    color: ${props => props.theme.colors.dark};
    font-size: ${props => props.theme.fontSizes.xl};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-bottom: ${props => props.theme.spacing.lg};
    
    h3 {
      font-size: ${props => props.theme.fontSizes.lg};
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.gray[600]};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.theme.borderRadius.md};
  transition: ${props => props.theme.transitions.default};
  
  &:hover {
    background: ${props => props.theme.colors.gray[100]};
    color: ${props => props.theme.colors.dark};
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const AddressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const AddressItem = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border: 2px solid ${props => props.theme.colors.gray[200]};
  border-radius: ${props => props.theme.borderRadius.lg};
  transition: ${props => props.theme.transitions.default};
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => props.theme.shadows.md};
  }
  
  h4 {
    margin: 0 0 ${props => props.theme.spacing.sm} 0;
    color: ${props => props.theme.colors.dark};
    font-size: ${props => props.theme.fontSizes.lg};
  }
  
  p {
    margin: 0 0 ${props => props.theme.spacing.md} 0;
    color: ${props => props.theme.colors.gray[600]};
    line-height: 1.6;
    font-size: ${props => props.theme.fontSizes.sm};
  }
  
  button {
    width: 100%;
    padding: ${props => props.theme.spacing.md};
    background: ${props => props.theme.colors.primary};
    color: white;
    border: none;
    border-radius: ${props => props.theme.borderRadius.md};
    font-size: ${props => props.theme.fontSizes.sm};
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${props => props.theme.spacing.sm};
    transition: ${props => props.theme.transitions.default};
    
    &:hover {
      background: ${props => props.theme.colors.secondary};
      transform: translateY(-2px);
      box-shadow: ${props => props.theme.shadows.md};
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.md};
    
    h4 {
      font-size: ${props => props.theme.fontSizes.base};
    }
    
    p {
      font-size: 13px;
      margin-bottom: ${props => props.theme.spacing.sm};
    }
    
    button {
      padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
      font-size: 13px;
    }
  }
`;

const PhoneNumbers = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.gray[700]};
  font-size: ${props => props.theme.fontSizes.base};
  align-items: center;
  
  a {
    color: inherit;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.xs};
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const Contact = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showAddressModal, setShowAddressModal] = useState(false);
  const { settings } = useSettings();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  // Remove the useEffect that fetches settings - now comes from context

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setApiError('');

    try {
      const payload = {
        name: `${data.firstName} ${data.lastName}`.trim(),
        email: data.email,
        phone: data.phone || undefined,
        subject: 'Website Contact Form',
        message: data.message
      };

      const res = await contactService.submitContact(payload);

      if (res && res.success) {
        setIsSubmitted(true);
        reset();
        // Hide success message after 5 seconds
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        setApiError(res?.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setApiError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Build address string from settings
  const getAddress = () => {
    if (!settings?.address) return '123 Tech Street, San Francisco, CA 94105';
    const { street, city, state, zipCode } = settings.address;
    return `${street || ''}, ${city || ''}, ${state || ''} ${zipCode || ''}`.replace(/,\s*,/g, ',').replace(/^,\s*|,\s*$/g, '').trim() || '123 Tech Street, San Francisco, CA 94105';
  };

  // Define multiple phone numbers and addresses
  const phoneNumbers = [
    ...(settings?.phone ? [{ number: settings.phone }] : []),
    ...(settings?.phoneSecondary ? [{ number: settings.phoneSecondary }] : [])
  ];

  const addresses = [
    {
      label: 'Main Office',
      address: getAddress(),
      isPrimary: true,
      link: settings?.address?.link || null
    },
    ...(settings?.additionalAddresses || []).map(addr => ({
      label: addr.label || 'Additional Location',
      address: `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zipCode || ''}`.replace(/,\s*,/g, ',').replace(/^,\s*|,\s*$/g, '').trim() || 'Address not set',
      isPrimary: false,
      link: addr.link || null
    }))
  ];

  const contactMethods = [
    {
      icon: <FiMail />,
      title: 'Email us',
      description: 'Send us an email and we\'ll respond within 24 hours.',
      action: 'Send Email',
      actionIcon: <FiMail />,
      link: `mailto:${settings?.email || 'hello@servicehub.com'}`,
      onClick: null
    },
    ...(phoneNumbers.length > 0 ? [{
      icon: <FiPhone />,
      title: 'Call us',
      description: phoneNumbers,
      action: 'Call Now',
      actionIcon: <FiPhone />,
      link: `tel:${phoneNumbers[0].number.replace(/\s/g, '')}`,
      onClick: null,
      isPhone: true
    }] : []),
    {
      icon: <FiMapPin />,
      title: 'Visit us',
      description: addresses.find(a => a.isPrimary)?.address || addresses[0]?.address || 'Address not configured',
      action: 'View Our Branches',
      actionIcon: <FiMapPin />,
      link: null,
      onClick: (e) => {
        e.preventDefault();
        setShowAddressModal(true);
      }
    }
  ];

  return (
    <ContactContainer>
      <PageHero
        title="Contact Us"
        subtitle="Have questions about our courses? Ready to start your learning journey? We're here to help you every step of the way."
        illustrationSrc="/contactus.png"
        illustrationAlt="Contact us illustration"
        rightHeight="360px"
        rightHeightTablet="300px"
        rightHeightMobile="240px"
        topPaddingTablet="1.25rem"
        topPaddingMobile="2rem"
        compact
      />

      <Section>
        <Container>
          <ScrollAnimation animation="fadeInUp">
            <ContactSection>
              <h2>Get in touch</h2>
              <p className="subtitle">
                Ready to help your company scale faster? Let's chat about how we can help.
              </p>
            </ContactSection>
          </ScrollAnimation>
            
            <ContactGrid>
              {contactMethods.map((method, index) => (
                <ScrollAnimation key={index} animation="fadeInUp" delay={index * 100}>
                  <ContactCard>
                    <div className="icon">
                      {method.icon}
                    </div>
                    <h3>{method.title}</h3>
                    {method.isPhone ? (
                      <PhoneNumbers>
                        {method.description.map((phone, i) => (
                          <a key={i} href={`tel:${phone.number.replace(/\s/g, '')}`}>
                            <FiPhone size={14} /> {phone.number}
                          </a>
                        ))}
                      </PhoneNumbers>
                    ) : (
                      <p>{method.description}</p>
                    )}
                    <a 
                      href={method.link || '#'} 
                      className="contact-action"
                      onClick={method.onClick}
                    >
                      {method.actionIcon}
                      {method.action}
                    </a>
                  </ContactCard>
                </ScrollAnimation>
              ))}
            </ContactGrid>

          <ScrollAnimation animation="fadeInUp" delay={300}>
            <MessageSection>
            <h2>Message us</h2>
            <p className="subtitle">
              We'll get back to you within 24 hours.
            </p>
          </MessageSection>

          <FormWrapper>
            {apiError && (
              <ErrorMessage>{apiError}</ErrorMessage>
            )}
            {isSubmitted && (
              <SuccessMessage>
                <FiCheckCircle />
                Thank you for your message! We'll get back to you within 24 hours.
              </SuccessMessage>
            )}

            <Form onSubmit={handleSubmit(onSubmit)}>
              <FormRow>
                <FormGroup>
                  <Label htmlFor="firstName">First name</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="First name"
                    error={errors.firstName}
                    {...register('firstName', {
                      required: 'First name is required'
                    })}
                  />
                  {errors.firstName && (
                    <ErrorMessage>{errors.firstName.message}</ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="lastName">Last name</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Last name"
                    error={errors.lastName}
                    {...register('lastName', {
                      required: 'Last name is required'
                    })}
                  />
                  {errors.lastName && (
                    <ErrorMessage>{errors.lastName.message}</ErrorMessage>
                  )}
                </FormGroup>
              </FormRow>

              <FormRow className="single">
                <FormGroup>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    error={errors.email}
                    {...register('email', {
                      required: 'Email address is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address'
                      }
                    })}
                  />
                  {errors.email && (
                    <ErrorMessage>{errors.email.message}</ErrorMessage>
                  )}
                </FormGroup>
              </FormRow>

              <FormRow className="single">
                <FormGroup>
                  <Label htmlFor="phone">Phone number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    error={errors.phone}
                    {...register('phone', {
                      pattern: {
                        value: /^[+]?[1-9][\d]{0,15}$/,
                        message: 'Please enter a valid phone number'
                      }
                    })}
                  />
                  {errors.phone && (
                    <ErrorMessage>{errors.phone.message}</ErrorMessage>
                  )}
                </FormGroup>
              </FormRow>

              <FormRow className="single">
                <FormGroup>
                  <Label htmlFor="message">Message</Label>
                  <TextArea
                    id="message"
                    placeholder="Tell us about your goals or questions..."
                    error={errors.message}
                    {...register('message', {
                      required: 'Please enter your message',
                      minLength: { value: 10, message: 'Message should be at least 10 characters' }
                    })}
                  />
                  {errors.message && (
                    <ErrorMessage>{errors.message.message || errors.message}</ErrorMessage>
                  )}
                </FormGroup>
              </FormRow>

              <SubmitButton
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    Send message
                    <FiSend />
                  </>
                )}
              </SubmitButton>
            </Form>
          </FormWrapper>
        </ScrollAnimation>
        </Container>
      </Section>

      {showAddressModal && (
        <Modal onClick={() => setShowAddressModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Our Locations</h3>
              <CloseButton onClick={() => setShowAddressModal(false)}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            <AddressList>
              {addresses.map((addr, index) => (
                <AddressItem key={index}>
                  <h4>{addr.label} {addr.isPrimary && '(Primary)'}</h4>
                  <p>{addr.address}</p>
                  <button onClick={() => window.open(addr.link || `https://maps.google.com/?q=${encodeURIComponent(addr.address)}`, '_blank')}>
                    <FiMapPin /> Get Directions
                  </button>
                </AddressItem>
              ))}
            </AddressList>
          </ModalContent>
        </Modal>
      )}
    </ContactContainer>
  );
};

export default Contact;